import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { stationsApi, type StationDetailOut } from '@/api/stations';
import { ApiError } from '@/api/client';
import { Display, Eyebrow } from '@/components/ui/Typography';
import { StatusPill } from '@/components/ui/StatusPill';
import { useStationSocket } from '@/hooks/useStationSocket';
import { friendlyMessage } from '@/lib/errors';
import { countAvailableConnectors } from '@/lib/stationStats';
import { VacuumCard } from '@/components/amenities/VacuumCard';
import { ChargerCard, chargerPeakKw } from '@/components/stations/ChargerCard';

// 5 s temporário enquanto Hub Gateway não restabelece push de
// `charger.status.changed` no WS. Voltar a 30 s assim que confirmarmos
// eventos via `bow_event_published station.status.changed` no log.
const POLL_FALLBACK_MS = 5_000;

export default function StationDetail() {
  const { stationId } = useParams();
  const navigate = useNavigate();
  // WS é a fonte primária — chega snapshot inicial e push em cada mudança.
  const liveStation = useStationSocket(stationId ?? null);
  // Fallback HTTP: 1ª request pra encurtar TTFB se WS demorar (handshake)
  // e poll a cada 30s pra garantir convergência se WS cair silenciosamente.
  const [pollStation, setPollStation] = useState<StationDetailOut | null>(null);
  const [error, setError] = useState<string | null>(null);

  const station = liveStation ?? pollStation;

  useEffect(() => {
    if (!stationId) return;
    let cancelled = false;

    const load = (isInitial: boolean) => {
      stationsApi
        .get(stationId)
        .then((s) => {
          if (cancelled) return;
          setPollStation(s);
          if (isInitial) setError(null);
        })
        .catch((err) => {
          if (cancelled) return;
          if (!isInitial) return;
          setError(
            err instanceof ApiError
              ? friendlyMessage(err, 'Falha ao carregar eletroposto')
              : 'Não foi possível conectar ao servidor'
          );
        });
    };

    load(true);
    const id = window.setInterval(() => load(false), POLL_FALLBACK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [stationId]);

  if (error && !station) {
    return (
      <div className="rounded-3xl bg-white p-5 text-sm text-bow-red shadow-soft">
        {error}
      </div>
    );
  }
  if (!station) {
    return <div className="h-48 animate-pulse rounded-3xl bg-white shadow-soft" />;
  }

  const { available: totalAvailable, total: totalConnectors } =
    countAvailableConnectors(station);

  // Foto: prefere banner_url 16:9 se o operador tiver, fallback pra quadrada
  const bannerUrl =
    (station.capabilities &&
      typeof station.capabilities['banner_url'] === 'string' &&
      (station.capabilities['banner_url'] as string)) ||
    station.photo_url;

  return (
    <div className="flex flex-col gap-5">
      {/* Banner com foto + nome sobreposto */}
      <div className="relative overflow-hidden rounded-3xl">
        {bannerUrl ? (
          <img
            src={bannerUrl}
            alt={station.name}
            className="h-56 w-full object-cover"
          />
        ) : (
          <div className="flex h-56 w-full items-center justify-center bg-bow-royal/10">
            <BoltGlyph size={64} fill="rgba(0,27,116,0.4)" />
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-bow-royal/90 via-bow-royal/40 to-transparent px-5 pb-5 pt-12">
          <Eyebrow color="rgba(241,237,228,0.75)">Estação</Eyebrow>
          <Display
            size={26}
            className="mt-1 leading-tight text-bow-cream"
            style={{ color: '#F1EDE4' }}
          >
            {station.name}
          </Display>
          <p className="mt-1 text-xs text-white/85">{station.address}</p>
        </div>
      </div>

      {/* Linha de status */}
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusPill tone={totalAvailable > 0 ? 'green' : 'muted'}>
          {totalAvailable} de {totalConnectors}{' '}
          {totalConnectors === 1 ? 'livre' : 'livres'}
        </StatusPill>
        {station.opening_hours && (
          <StatusPill tone="muted">{station.opening_hours}</StatusPill>
        )}
      </div>

      {/* Lista de carregadores — ordenada do mais rápido pro mais lento */}
      <div className="flex flex-col gap-3">
        {/* "Escolha seu carregador" em vez de "Conectores" — cliente não
            entendia o que Rápido/Padrão significavam nem que era pra tocar
            (feedback 2026-07-02 nº 8, ajustado por Saulo 2026-07-04). */}
        <Display size={18} as="h2">
          Escolha seu carregador
        </Display>
        {station.chargers.length === 0 ? (
          <p className="text-sm text-bow-royal/65">
            Nenhum carregador disponível neste eletroposto.
          </p>
        ) : (
          [...station.chargers]
            .sort((a, b) => chargerPeakKw(b) - chargerPeakKw(a))
            .map((c) => (
              <ChargerCard
                key={c.id}
                charger={c}
                onSelect={(connectorId) => navigate(`/configure/${c.id}/${connectorId}`)}
              />
            ))
        )}
      </div>

      {/* Comodidades pagas (aspirador) — só quando a estação tem a capability
          `vacuum`. O card se esconde sozinho se não houver device habilitado. */}
      {stationId && station.capabilities?.['vacuum'] === true && (
        <VacuumCard stationId={stationId} />
      )}
    </div>
  );
}

function BoltGlyph({ size = 22, fill = 'currentColor' }: { size?: number; fill?: string }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill={fill} aria-hidden>
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}
